/**
 * @file     pageContent.ts
 * @readme   ./README.md
 * @purpose  i18n content schema for /properties/[key] (MLS detail) page. All user-facing strings via localizedText under pages.propertyDetail.* namespace.
 */

import { localizedText } from '@/core/content';

export const propertyDetailContent = {
  breadcrumb: {
    home: localizedText('pages.propertyDetail.breadcrumb.home', 'Home'),
    properties: localizedText('pages.propertyDetail.breadcrumb.properties', 'Properties'),
  },
  summary: {
    beds: localizedText('pages.propertyDetail.summary.beds', '{{count}} bed'),
    baths: localizedText('pages.propertyDetail.summary.baths', '{{count}} bath'),
    livingArea: localizedText('pages.propertyDetail.summary.livingArea', '{{sqft}} sq ft'),
    yearBuilt: localizedText('pages.propertyDetail.summary.yearBuilt', 'Built {{year}}'),
    lotSize: localizedText('pages.propertyDetail.summary.lotSize', 'Lot {{sqft}} sq ft'),
    pricePerSqft: localizedText('pages.propertyDetail.summary.pricePerSqft', '{{price}}/sq ft'),
    daysOnMarket: localizedText('pages.propertyDetail.summary.daysOnMarket', '{{count}}d on market'),
    listedToday: localizedText('pages.propertyDetail.summary.listedToday', 'Listed today'),
    mlsId: localizedText('pages.propertyDetail.summary.mlsId', 'MLS® {{id}}'),
  },
  chips: {
    newListing: localizedText('pages.propertyDetail.chips.newListing', 'New listing'),
    openHouse: localizedText('pages.propertyDetail.chips.openHouse', '{{label}}'),
  },
  cta: {
    share: localizedText('pages.propertyDetail.cta.share', 'Share'),
    shareCopied: localizedText(
      'pages.propertyDetail.cta.shareCopied',
      'Link copied to clipboard',
    ),
    preApproved: localizedText('pages.propertyDetail.cta.preApproved', 'Information On Financing'),
    contact: localizedText('pages.propertyDetail.cta.contact', 'Inquire'),
  },
  remarks: {
    title: localizedText('pages.propertyDetail.remarks.title', 'About this home'),
    empty: localizedText('pages.propertyDetail.remarks.empty', 'No description available.'),
  },
  openHouses: {
    title: localizedText('pages.propertyDetail.openHouses.title', 'Open houses'),
  },
  similar: {
    title: localizedText('pages.propertyDetail.similar.title', 'More homes in {{place}}'),
  },
  sections: {
    anchors: {
      label: localizedText('pages.propertyDetail.sections.anchors.label', 'On this page'),
      overview: localizedText('pages.propertyDetail.sections.anchors.overview', 'Overview'),
      price: localizedText('pages.propertyDetail.sections.anchors.price', 'Price'),
      history: localizedText('pages.propertyDetail.sections.anchors.history', 'History'),
      neighbourhood: localizedText('pages.propertyDetail.sections.anchors.neighbourhood', 'Neighbourhood'),
      documents: localizedText('pages.propertyDetail.sections.anchors.documents', 'Documents'),
      costs: localizedText('pages.propertyDetail.sections.anchors.costs', 'Costs'),
      similar: localizedText('pages.propertyDetail.sections.anchors.similar', 'Similar homes'),
    },
    openHouses: localizedText('pages.propertyDetail.sections.openHouses', 'Open houses'),
    about: localizedText('pages.propertyDetail.sections.about', 'About this home'),
    facts: localizedText('pages.propertyDetail.sections.facts', 'Home facts'),
    space: localizedText('pages.propertyDetail.sections.space', 'Space and layout'),
    spaceGateTitle: localizedText('pages.propertyDetail.sections.spaceGateTitle', 'Room sizes and building details need a free account'),
    spaceGateBody: localizedText('pages.propertyDetail.sections.spaceGateBody', 'See room sizes, the floor-by-floor split, construction and the lot with a free account.'),
    valueGateTitle: localizedText('pages.propertyDetail.sections.valueGateTitle', 'Home value figures need a free account'),
    valueGateBody: localizedText('pages.propertyDetail.sections.valueGateBody', 'See how this home is valued with a free account.'),
    gateCta: localizedText('pages.propertyDetail.sections.gateCta', 'Create account'),
    gateCtaAuthed: localizedText('pages.propertyDetail.sections.gateCtaAuthed', 'Turn on access'),
    history: localizedText('pages.propertyDetail.sections.history', 'Listing history'),
    value: localizedText('pages.propertyDetail.sections.value', 'Home value'),
    neighbourhood: localizedText('pages.propertyDetail.sections.neighbourhood', 'Neighbourhood'),
    demographics: localizedText('pages.propertyDetail.sections.demographics', 'Who lives here'),
    documents: localizedText('pages.propertyDetail.sections.documents', 'Documents'),
    costs: localizedText('pages.propertyDetail.sections.costs', 'Monthly and upfront costs'),
    originalListPrice: localizedText('pages.propertyDetail.sections.originalListPrice', 'Original list price'),
    features: localizedText('pages.propertyDetail.sections.features', 'Features'),
    similarNearby: localizedText('pages.propertyDetail.sections.similarNearby', 'Similar homes nearby'),
    similarNearbyIntro: localizedText(
      'pages.propertyDetail.sections.similarNearbyIntro',
      'Other {{type}}s in or near {{neighbourhood}}.',
    ),
    curatedAsk: localizedText('pages.propertyDetail.sections.curatedAsk', 'Ask about this home'),
    curatedSimilar: localizedText('pages.propertyDetail.sections.curatedSimilar', 'See similar homes'),
    curatedAlerts: localizedText('pages.propertyDetail.sections.curatedAlerts', 'Get alerts for this area'),
    pendingTitle: localizedText('pages.propertyDetail.sections.pendingTitle', 'Pending'),
    pendingBody: localizedText('pages.propertyDetail.sections.pendingBody', 'This home has an accepted offer.'),
    soldTitle: localizedText('pages.propertyDetail.sections.soldTitle', 'Sold'),
    soldBody: localizedText('pages.propertyDetail.sections.soldBody', 'This home is no longer for sale.'),
  },
  attribution: {
    listedBy: localizedText(
      'pages.propertyDetail.attribution.listedBy',
      'Listed by {{agent}}, {{office}}.',
    ),
    listedByAgentOnly: localizedText(
      'pages.propertyDetail.attribution.listedByAgentOnly',
      'Listed by {{agent}}.',
    ),
    listedByOfficeOnly: localizedText(
      'pages.propertyDetail.attribution.listedByOfficeOnly',
      'Listed by {{office}}.',
    ),
    dataSource: localizedText(
      'pages.propertyDetail.attribution.dataSource',
      'Data from BCRES.',
    ),
    lastUpdated: localizedText(
      'pages.propertyDetail.attribution.lastUpdated',
      'Last updated {{timestamp}}.',
    ),
  },
  fullDetails: {
    title: localizedText('pages.propertyDetail.fullDetails.title', 'There is more to this listing.'),
    body: localizedText(
      'pages.propertyDetail.fullDetails.body',
      'Create a free account to see the exact location, taxes, room sizes and price history.',
    ),
    cta: localizedText('pages.propertyDetail.fullDetails.cta', 'Sign in'),
  },
  save: {
    cta: localizedText('pages.propertyDetail.save.cta', 'Like'),
    ctaSaved: localizedText('pages.propertyDetail.save.ctaSaved', 'Liked ✓'),
  },
  gallery: {
    a11yLabel: localizedText('pages.propertyDetail.gallery.a11yLabel', 'Listing photo gallery'),
    counter: localizedText('pages.propertyDetail.gallery.counter', '{{current}} of {{total}}'),
    next: localizedText('pages.propertyDetail.gallery.next', 'Next photo'),
    prev: localizedText('pages.propertyDetail.gallery.prev', 'Previous photo'),
    close: localizedText('pages.propertyDetail.gallery.close', 'Close photo'),
    photoAlt: localizedText(
      'pages.propertyDetail.gallery.photoAlt',
      '{{title}}, photo {{current}} of {{total}}',
    ),
    noPhotos: localizedText('pages.propertyDetail.gallery.noPhotos', 'No photos available.'),
    counterShort: localizedText('pages.propertyDetail.gallery.counterShort', '{{current}} / {{total}}'),
    photoAltAt: localizedText('pages.propertyDetail.gallery.photoAltAt', 'Photo {{current}} of {{total}} at {{title}}'),
    photoAltCaption: localizedText('pages.propertyDetail.gallery.photoAltCaption', '{{caption}} at {{title}}'),
    openPhoto: localizedText('pages.propertyDetail.gallery.openPhoto', 'Open photo {{current}} of {{total}}'),
    showAll: localizedText('pages.propertyDetail.gallery.showAll', 'Show all {{count}} photos'),
    moreCount: localizedText('pages.propertyDetail.gallery.moreCount', '+{{count}}'),
    photoCount: localizedText('pages.propertyDetail.gallery.photoCount', '{{count}} photos'),
    tabsLabel: localizedText('pages.propertyDetail.gallery.tabsLabel', 'Photos and media'),
    tabPhotos: localizedText('pages.propertyDetail.gallery.tabPhotos', 'Photos'),
    tabTour: localizedText('pages.propertyDetail.gallery.tabTour', 'Virtual tour'),
    tabTourShort: localizedText('pages.propertyDetail.gallery.tabTourShort', 'Tour'),
    tabStreet: localizedText('pages.propertyDetail.gallery.tabStreet', 'Street view'),
    tabStreetShort: localizedText('pages.propertyDetail.gallery.tabStreetShort', 'Street'),
    tabMap: localizedText('pages.propertyDetail.gallery.tabMap', 'Map'),
    addToList: localizedText('pages.propertyDetail.gallery.addToList', 'Add to list'),
    onList: localizedText('pages.propertyDetail.gallery.onList', 'On your list'),
    share: localizedText('pages.propertyDetail.gallery.share', 'Share'),
    filmstrip: localizedText('pages.propertyDetail.gallery.filmstrip', 'All photos'),
    endTitle: localizedText('pages.propertyDetail.gallery.endTitle', 'See it in person'),
    endBody: localizedText('pages.propertyDetail.gallery.endBody', "You've seen all {{total}} photos."),
    startAgain: localizedText('pages.propertyDetail.gallery.startAgain', 'Start again'),
    tourStart: localizedText('pages.propertyDetail.gallery.tourStart', 'Start the virtual tour'),
    tourNote: localizedText('pages.propertyDetail.gallery.tourNote', 'Opens in a new tab'),
    streetBody: localizedText('pages.propertyDetail.gallery.streetBody', 'Look around the street in Google Maps.'),
    streetOpen: localizedText('pages.propertyDetail.gallery.streetOpen', 'Open street view'),
    photoLoading: localizedText('pages.propertyDetail.gallery.photoLoading', 'Loading photo'),
    photoFailed: localizedText('pages.propertyDetail.gallery.photoFailed', 'This photo did not load.'),
    retry: localizedText('pages.propertyDetail.gallery.retry', 'Try again'),
    noPhotosYet: localizedText('pages.propertyDetail.gallery.noPhotosYet', 'No photos yet'),
    seeMap: localizedText('pages.propertyDetail.gallery.seeMap', 'See the map'),
    back: localizedText('pages.propertyDetail.gallery.back', 'Back to listing'),
  },
  match: {
    title: localizedText('pages.propertyDetail.match.title', 'Why this might match you'),
    loading: localizedText('pages.propertyDetail.match.loading', 'Computing match…'),
    notMatched: localizedText(
      'pages.propertyDetail.match.notMatched',
      "This listing didn't match your profile because:",
    ),
    error: localizedText(
      'pages.propertyDetail.match.error',
      'Match explanation unavailable.',
    ),
    empty: localizedText(
      'pages.propertyDetail.match.empty',
      'No match information for this listing.',
    ),
  },
  priceTimeline: {
    title: localizedText('pages.propertyDetail.priceTimeline.title', 'Price history'),
    event: {
      listed: localizedText('pages.propertyDetail.priceTimeline.event.listed', 'Listed'),
      priceChange: localizedText('pages.propertyDetail.priceTimeline.event.priceChange', 'Price change'),
      statusChange: localizedText('pages.propertyDetail.priceTimeline.event.statusChange', 'Status change'),
      expired: localizedText('pages.propertyDetail.priceTimeline.event.expired', 'Expired'),
      terminated: localizedText('pages.propertyDetail.priceTimeline.event.terminated', 'Terminated'),
      sold: localizedText('pages.propertyDetail.priceTimeline.event.sold', 'Sold'),
      pending: localizedText('pages.propertyDetail.priceTimeline.event.pending', 'Pending'),
    },
  },
  cmhcOutlook: {
    title: localizedText('pages.propertyDetail.cmhcOutlook.title', '{{geo}} market outlook'),
    attribution: localizedText('pages.propertyDetail.cmhcOutlook.attribution', 'From the CMHC Housing Market Outlook.'),
  },
  investorLens: {
    title: localizedText('pages.propertyDetail.investorLens.title', 'Investor lens'),
    yieldHeading: localizedText('pages.propertyDetail.investorLens.yieldHeading', 'Gross rental yield (rough)'),
    yieldDisclaimer: localizedText('pages.propertyDetail.investorLens.yieldDisclaimer', 'Before vacancy, fees and management. A rough check only.'),
    disclaimer: localizedText('pages.propertyDetail.investorLens.disclaimer', 'This view weighs carrying cost and market temperature more, and price history less.'),
    bucket: {
      smoking_hot: localizedText('pages.propertyDetail.investorLens.bucket.smokingHot', 'Strong investor signal'),
      worth_a_look: localizedText('pages.propertyDetail.investorLens.bucket.worthALook', 'Worth running numbers'),
      fair_value: localizedText('pages.propertyDetail.investorLens.bucket.fairValue', 'Market-rate investor'),
      skip: localizedText('pages.propertyDetail.investorLens.bucket.skip', 'Thin margin signals'),
    },
    componentLabel: {
      price_vs_comps: localizedText('pages.propertyDetail.investorLens.label.priceVsComps', 'Price vs nearby asking'),
      price_history: localizedText('pages.propertyDetail.investorLens.label.priceHistory', 'Price history'),
      hpi_deviation: localizedText('pages.propertyDetail.investorLens.label.hpiDeviation', 'Vs area benchmark'),
      market_context: localizedText('pages.propertyDetail.investorLens.label.marketContext', 'Market temperature'),
      carrying_cost: localizedText('pages.propertyDetail.investorLens.label.carryingCost', 'Carrying cost'),
      bc_assessment: localizedText('pages.propertyDetail.investorLens.label.bcAssessment', 'BC assessment'),
    },
  },
  dealContext: {
    title: localizedText('pages.propertyDetail.dealContext.title', 'How the price compares'),
    scoreLabel: localizedText('pages.propertyDetail.dealContext.scoreLabel', 'Deal score'),
    scoredAt: localizedText('pages.propertyDetail.dealContext.scoredAt', 'Scored {{date}}'),
    bucket: {
      smoking_hot: localizedText('pages.propertyDetail.dealContext.bucket.smokingHot', 'Strong value signals'),
      worth_a_look: localizedText('pages.propertyDetail.dealContext.bucket.worthALook', 'Good value signals'),
      fair_value: localizedText('pages.propertyDetail.dealContext.bucket.fairValue', 'In line with market'),
      skip: localizedText('pages.propertyDetail.dealContext.bucket.skip', 'Weak value signals'),
    },
    componentLabel: {
      price_vs_comps: localizedText('pages.propertyDetail.dealContext.label.priceVsComps', 'Price vs nearby asking'),
      price_history: localizedText('pages.propertyDetail.dealContext.label.priceHistory', 'Price history'),
      hpi_deviation: localizedText('pages.propertyDetail.dealContext.label.hpiDeviation', 'Vs area benchmark'),
      market_context: localizedText('pages.propertyDetail.dealContext.label.marketContext', 'Market temperature'),
      carrying_cost: localizedText('pages.propertyDetail.dealContext.label.carryingCost', 'Carrying cost'),
      bc_assessment: localizedText('pages.propertyDetail.dealContext.label.bcAssessment', 'BC assessment'),
    },
    nhpiCaption: localizedText('pages.propertyDetail.dealContext.nhpiCaption', 'Vancouver new-home price index, last 12 months.'),
    disclaimer: localizedText('pages.propertyDetail.dealContext.disclaimer', 'Based on nearby asking prices, price changes, area benchmarks and carrying cost. Use it as one input.'),
  },
};
